import { Badge, Box, Button, Drawer, FormControl, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from '@mui/material'
import { CommonLayoutForPOA } from '../commonComponents'
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../utils/useDebounce';
import { DialogCloseIcon, FilterIcon } from '../../assets/SvgIcons';
import { SearchIcon } from '../customSVGs';
import CustomFilter from '../investorOverview/common/CustomFilter';
import { Order, PaginationObj, SortingObj, TableHeaderData } from '../investorOverview/types';
import PaymentsApprovalTableBody from './paymentsApprovalTableBody';
import { PaymentApprovalListObj, PaymentTypeFilter, ProductTypeFilter } from '../../redux-store/types/orderManagement';
import { getPaymentApprovalList } from '../../redux-store/actions/orderApproval';
import { PaymentApprovalListRes } from '../../redux-store/types/orderApproval';
import CustomTable from '../investorOverview/common/customTable';
import { getPaymentTypeFilter, getProductTypeFilter } from '../../redux-store/actions/orderManagement';
import { whiteSpaceOnlyRegex } from '../../utils/regex';

const tableHeaderData: TableHeaderData[] = [
    {
        id: 'orderNumber',
        label: 'ORDER NUMBER',
        align: 'center',
        isSortable: true
    },
    {
        id: 'orderDate',
        label: 'ORDER DATE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'clientName',
        label: 'INVESTOR NAME',
        align: 'center',
        isSortable: true
    },
    {
        id: 'productType',
        label: 'PRODUCT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'portfolioName',
        label: 'ACCOUNT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'accountType',
        label: 'ACCOUNT TYPE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'approvalStatus',
        label: 'ORDER STATUS',
        align: 'center',
        isSortable: true
    },
    {
        id: 'paymentStatus',
        label: 'PAYMENT STATUS',
        align: 'center',
        isSortable: true
    },
]

export default function PaymentsApproval(): JSX.Element {
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const [paymentApprovalLoading, setPaymentApprovalLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const debounceSearchText = useDebounce(searchText, 500);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    const [tableData, setTableData] = useState<PaymentApprovalListObj[]>([]);
    const [productFilterData, setProductFilterData] = useState<ProductTypeFilter[]>([])
    const [paymentFilterData, setPaymentFilterData] = useState<PaymentTypeFilter[]>([])
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);

    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })

    const [totalFilters, setTotalFilters] = useState<number>(0);

    const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map((obj: ProductTypeFilter) => `${obj.orderStatusId}`)

    const paymentFilterPayload = paymentFilterData?.filter(obj => obj.checked)?.map((obj: PaymentTypeFilter) => `${obj.orderStatusId}`)


    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
    }

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true)
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }





    const fetchPaymentApprovalList = async ({
        sort = "",
        order = "",
        productType = [],
        paymentStatus = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: any) => {
        try {
            setPaymentApprovalLoading(true);

            const payload = {
                productType,
                paymentStatus,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage
            }

            const paymentApprovalListRes = await (dispatch(getPaymentApprovalList(payload)) as unknown as Promise<PaymentApprovalListRes>)

            const { orders, totalCount } = paymentApprovalListRes;

            setTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error', error);
        } finally {
            setPaymentApprovalLoading(false);
        }
    }


    const handleFiltersClearAll = () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setPaymentFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        setTotalFilters(0)

        fetchPaymentApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            searchInput: debounceSearchText?.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
        setFilterDrawerOpen(false)
    }

    const handleProductTypeFilterChange = (value: string, checked: boolean) => {
        setProductFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handlePaymentTypeFilterChange = (value: string, checked: boolean) => {
        setPaymentFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleSort = async (orderBy: string, order: Order) => {

        setTableSortingObj({
            order,
            orderBy
        })

        fetchPaymentApprovalList({
            sort: orderBy,
            order,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            searchInput: debounceSearchText?.trim(),
            currentPage: tablePaginationObj?.currentPage + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))
        fetchPaymentApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            searchInput: debounceSearchText?.trim(),
            rowsPerPage: Number(event.target.value),
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchPaymentApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            searchInput: debounceSearchText?.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }


    const handleApplyFilters = () => {
        setFilterDrawerOpen(false)

        const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId)

        const paymentFilterPayload = paymentFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId);

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
        const totalAppliedFilters =
            (productFilterPayload?.length ?? 0) + (paymentFilterPayload?.length ?? 0);

        setTotalFilters(totalAppliedFilters)

        fetchPaymentApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            paymentStatus: paymentFilterPayload,
            searchInput: debounceSearchText?.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const isFilterApplied = productFilterData?.some(obj => obj.checked) || paymentFilterData?.some(obj => obj.checked)


    useEffect(() => {
        async function fetchData() {
            if (productFilterData?.length === 0 || paymentFilterData?.length === 0) {
                try {
                    setIsFiltersLoading(true);
                    const productFilterRes = await (dispatch(getProductTypeFilter()) as unknown as Promise<ProductTypeFilter[]>)
                    setProductFilterData(productFilterRes);

                    const paymentFilterRes = await (dispatch(getPaymentTypeFilter()) as unknown as Promise<PaymentTypeFilter[]>)
                    setPaymentFilterData(paymentFilterRes?.map(obj => {
                        if (obj?.orderStatusId?.toLowerCase() === "pending") {
                            return { ...obj, checked: true }
                        }
                        return obj
                    }));

                    await fetchPaymentApprovalList({
                        sort: tableSortingObj?.orderBy,
                        order: tableSortingObj?.order,
                        productType: productFilterRes
                            ?.filter(obj => obj.checked)
                            ?.map(obj => obj?.orderStatusId),
                        paymentStatus: paymentFilterRes
                            ?.map(obj => {
                                if (obj?.orderStatusId?.toLowerCase() === "pending") {
                                    return { ...obj, checked: true }
                                }
                                return obj
                            })
                            ?.filter(obj => obj?.checked)
                            ?.map(obj => obj?.orderStatusId),
                        searchInput: debounceSearchText.trim(),
                        currentPage: 1,
                        rowsPerPage: tablePaginationObj?.rowsPerPage,
                    })
                    setTotalFilters(1);
                } catch (e) {
                    console.log('error', e);
                }
                finally {
                    setIsFiltersLoading(false);
                }
                return
            }

            await fetchPaymentApprovalList({
                sort: tableSortingObj?.orderBy,
                order: tableSortingObj?.order,
                productType: productFilterPayload,
                paymentStatus: paymentFilterPayload,
                searchInput: debounceSearchText?.trim(),
                currentPage: 1,
                rowsPerPage: tablePaginationObj?.rowsPerPage,
            })
        }

        fetchData()
    }, [debounceSearchText])

    return (
        <CommonLayoutForPOA>
            <>
                <Box>
                    <Typography sx={{ color: 'text.primary', mb: 2.5 }} variant='h1'>
                        Payment Approvals
                    </Typography>

                    <Box sx={{ background: palette.background.paper, borderRadius: '25px', overflow: 'hidden', padding: '30px 25px' }}>
                        <Box sx={{ mb: '35px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                            <FormControl variant="standard" sx={{ display: 'block' }}>
                                <TextField
                                    id="outlined-multiline-flexible"
                                    value={searchText}
                                    placeholder={`Search  Payment`}

                                    onChange={handleSearchTextChange}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                                    onClick={handleClearSearch} aria-label="Clear search"
                                                >
                                                    <DialogCloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        style: {
                                            borderRadius: '5px',
                                            height: '40px',
                                            width: '100%',
                                            maxWidth: '255px',
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            </FormControl>
                            <Button
                                variant="text"
                                startIcon={
                                    <Badge
                                        showZero
                                        badgeContent={totalFilters}
                                        sx={(theme: Theme) => ({
                                            '& .MuiBadge-badge': {
                                                right: 0,
                                                top: -3,
                                                backgroundColor: theme.palette.primary[500],
                                                color: theme.palette.common.white,
                                                ...theme.typography.subtitle_600
                                            },
                                        })}
                                    >
                                        <FilterIcon style={{ opacity: 1 }} />
                                    </Badge>
                                }
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    padding: "5px",
                                    ...theme.typography.body_500,
                                    "&:hover": {
                                        backgroundColor: theme.palette.grey[50]
                                    }
                                })}
                                TouchRippleProps={{
                                    style: {
                                        color: palette.grey[300]
                                    }
                                }}
                                onClick={handleOpenFilterDrawer}
                                disabled={false}
                            >
                                Filters
                            </Button>
                        </Box>
                        <CustomTable
                            isLoading={paymentApprovalLoading || isFiltersLoading}
                            tableBody={
                                <PaymentsApprovalTableBody data={tableData ?? []} />
                            }
                            tableData={tableData ?? []}
                            tableHeaderData={tableHeaderData}
                            isPaginationVisible={true}
                            tableDataCount={tablePaginationObj?.totalRows ?? 0}
                            handleSort={handleSort}
                            order={tableSortingObj.order}
                            orderBy={tableSortingObj.orderBy}
                            handleRowsPerPageChange={handleRowsPerPageChange}
                            handlePageChange={handlePageChange}
                            currentPage={tablePaginationObj.currentPage}
                            rowsPerPageOptions={[5, 10, 20, 30]}
                            rowsPerPage={tablePaginationObj.rowsPerPage}
                            tableBodyLoaderRowCustomStyles={{
                                '& td:first-of-type': {
                                    paddingLeft: "0px"
                                },
                            }}
                            tableHeadRowCustomStyles={{
                                '& th:first-of-type': {
                                    paddingLeft: "0px"
                                }
                            }}
                            tableBodyCustomStyles={{
                                '& tr:last-child td': {
                                    border: 0
                                }
                            }}
                            loadingRowsNum={5}
                        />
                    </Box>
                </Box>
                <Drawer
                    open={isFilterDrawerOpen}
                    onClose={handleCloseFilterDrawer}
                    anchor="right"
                    PaperProps={{
                        sx: {
                            padding: "25px",
                            width: "450px"
                        }
                    }}
                    ModalProps={{
                        sx: (theme: Theme) => ({
                            backgroundColor: `${theme.palette.common.black}03`
                        })
                    }}
                >
                    <CustomFilter
                        isLoading={isFiltersLoading}
                        handleClearAll={handleFiltersClearAll}
                        handleApplyFilters={handleApplyFilters}
                        showCloseIcon={true}
                        handleCloseIconClick={handleCloseFilterDrawer}
                        showAmcFilter={false}
                        showCategoryFilter={false}
                        showRiskFilter={false}
                        showProductTypeFilter={true}
                        showPaymentTypeFilter={true}
                        showApprovalTypeFilter={false}
                        showTransactionTypeFilter={false}
                        productTypeFilterData={productFilterData}
                        paymentFilterData={paymentFilterData}
                        handleProductTypeFilterChange={handleProductTypeFilterChange}
                        handlePaymentTypeFilterChange={handlePaymentTypeFilterChange}
                        isFilterApplied={isFilterApplied}
                        defaultExpandedFilter="productType"
                    />
                </Drawer>
            </>
        </CommonLayoutForPOA>
    )
}
